import React, { useState, useEffect, useRef, useMemo } from "react";
import { Form, ListGroup, Spinner, Badge } from "react-bootstrap";
import { debounce } from "lodash";
import ItemService from "../../../../services/customer_appCommonService";
import toast from "react-hot-toast";

const ERFAutocompleteSearch = ({
  onERFSelect,
  disabled = false,
  placeholder = "Type patient name or ERF number (min. 2 characters)...",
}) => {
  // State Management
  const [searchInput, setSearchInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Refs
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // API Call Function
  const searchERFs = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);

    try {
      const payload = {
        queryName: "ERFList",
        page: 1,
        per_page: 50,
        timeZone: "Asia/Calcutta",
        searchTerm: searchTerm.trim(),
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        setSuggestions(response.data);
        setShowDropdown(response.data.length > 0);
        setSelectedIndex(-1);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error("ERF Search Error:", err);
      setSuggestions([]);
      setShowDropdown(false);
      toast.error("Error searching ERFs");
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced Search
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        searchERFs(value);
      }, 400),
    []
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Handle Input Change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (value.trim().length >= 2) {
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  // Handle Selection
  const handleSelectSuggestion = (erf) => {
    setSearchInput(`${erf.documentNumber} - ${erf.patientName}`);
    setShowDropdown(false);
    setSuggestions([]);
    setSelectedIndex(-1);

    if (onERFSelect) {
      onERFSelect(erf);
    }
    toast.success(`Selected ERF: ${erf.documentNumber}`);
  };

  // Clear Search
  const handleClear = () => {
    setSearchInput("");
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);
    debouncedSearch.cancel();
  };

  // Keyboard Navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll(".list-group-item");
      const selectedElement = items[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex]);

  // Format Date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN");
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="position-relative">
      {/* Search Input */}
      <div className="position-relative">
        <Form.Control
          ref={searchInputRef}
          type="text"
          placeholder={placeholder}
          value={searchInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
          disabled={disabled}
          className="pe-5"
        />

        {/* Loading Spinner */}
        {isSearching && (
          <div
            className="position-absolute top-50 end-0 translate-middle-y me-3"
            style={{ pointerEvents: "none" }}
          >
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        )}

        {/* Clear Button */}
        {searchInput && !isSearching && (
          <button
            type="button"
            className="btn btn-link text-danger position-absolute top-50 end-0 translate-middle-y p-0 me-2"
            onClick={handleClear}
            style={{ textDecoration: "none" }}
          >
            <i className="bi bi-x-circle-fill"></i>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
          style={{
            zIndex: 1050,
            maxHeight: "350px",
            overflowY: "auto",
          }}
        >
          <ListGroup variant="flush">
            {suggestions.map((erf, index) => (
              <ListGroup.Item
                key={erf._id || index}
                action
                active={index === selectedIndex}
                onClick={() => handleSelectSuggestion(erf)}
                className="border-0"
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    {/* ERF Number & Status */}
                    <div className="mb-2">
                      <Badge bg="primary" className="me-2">
                        {erf.documentNumber || erf.equipmentRentNr}
                      </Badge>
                      <Badge
                        bg={
                          erf.equipmentStatus === "Created"
                            ? "success"
                            : "secondary"
                        }
                      >
                        {erf.equipmentStatus || erf.status}
                      </Badge>
                    </div>

                    {/* Patient Name */}
                    <div className="fw-bold mb-1">
                      <i className="bi bi-person-fill me-1"></i>
                      {erf.patientName}
                    </div>

                    {/* Contact & Age */}
                    <div className="text-muted small">
                      <span className="me-3">
                        <i className="bi bi-telephone me-1"></i>
                        {erf.contactNumber || "N/A"}
                      </span>
                      {erf.age && (
                        <span>
                          <i className="bi bi-calendar me-1"></i>
                          {erf.age} yrs
                        </span>
                      )}
                    </div>

                    {/* Date & Doctor */}
                    <div className="text-muted small mt-2 pt-2 border-top">
                      <span className="me-3">
                        <i className="bi bi-calendar-event me-1"></i>
                        {formatDate(erf.orderDate)}
                      </span>
                      {erf.doctorName && (
                        <span>
                          <i className="bi bi-person-badge me-1"></i>
                          Dr. {erf.doctorName}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="ms-3">
                    <i className="bi bi-arrow-right-circle text-primary"></i>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default ERFAutocompleteSearch;